import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Scales, FileText, ShieldCheck, Warning, PencilSimple, Clock, Eye, ArrowsHorizontal, UserFocus,
  Buildings, Scroll, Copyright,
} from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";
import { paragraphDiff } from "@/lib/paragraphDiff";

const SLOT_META = {
  header: { icon: Scales, desc: "Appears immediately below the company banner on every Company Profile. Primary FCA financial promotion." },
  footer: { icon: FileText, desc: "Compact regulatory strip at the foot of every Company Profile — document version, effective date and regulator reference." },
  confidential: { icon: ShieldCheck, desc: "Confirmation modal shown before any confidential document opens." },
  sign_in: { icon: Warning, desc: "Short access notice shown on sign-in / registration and inside the first-login investor declaration." },
  privacy_policy: { icon: Scroll, desc: "Served at /privacy. Linked from every page footer." },
  terms_and_conditions: { icon: FileText, desc: "Served at /terms. Linked from every page footer." },
  copyright_notice: { icon: Copyright, desc: "Served at /copyright. Linked from every page footer." },
  company_info: { icon: Buildings, desc: "Registered office, FCA reference and contact information used across the site footer." },
};

const AUDIENCE_META = {
  professional: { label: "Professional Investor", tone: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
  retail: { label: "Retail Investor", tone: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  hnw: { label: "High Net Worth Investor", tone: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300" },
  self_certified: { label: "Self-Certified Sophisticated Investor", tone: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
};

const CATEGORY_ICON = { fca: Scales, legal: Scroll, corporate: Buildings };

export default function AdminLegal() {
  const [data, setData] = useState({});
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("fca");
  const [editSlot, setEditSlot] = useState(null);
  const [editAudience, setEditAudience] = useState("professional");
  const [activeAudienceBySlot, setActiveAudienceBySlot] = useState({});
  const [preview, setPreview] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", version: "", effective_at: "", enabled: true, notes: "", audience: "professional" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get("/admin/disclaimers"),
        api.get("/admin/disclaimers/categories"),
      ]);
      setData(d.data);
      setCategories(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const currentSlots = categories[tab]?.slots || [];
  const supportsAudience = categories[tab]?.supports_audience !== false;

  const getActiveAudience = (slot) => activeAudienceBySlot[slot] || "professional";
  const setActiveAudience = (slot, aud) => setActiveAudienceBySlot((prev) => ({ ...prev, [slot]: aud }));

  const openEdit = (slot, audience) => {
    const active = data[slot]?.audiences?.[audience]?.active;
    setEditAudience(audience);
    setEditSlot(slot);
    setForm({
      title: active?.title || "",
      body: active?.body || "",
      version: "",
      effective_at: new Date().toISOString().slice(0, 10),
      enabled: active?.enabled ?? true,
      notes: "",
      audience,
    });
  };

  const publish = async () => {
    if (!form.body.trim()) { toast.error("Body cannot be empty"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/disclaimers/${editSlot}`, {
        title: form.title || null,
        body: form.body,
        version: form.version || null,
        audience: form.audience,
        effective_at: form.effective_at ? new Date(form.effective_at).toISOString() : null,
        enabled: form.enabled,
        notes: form.notes || null,
      });
      toast.success("New version published");
      setEditSlot(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setBusy(false); }
  };

  const toggleEnabled = async (disclaimerId, enabled) => {
    try {
      await api.patch(`/admin/disclaimers/${disclaimerId}/enable?enabled=${enabled}`);
      toast.success(enabled ? "Enabled" : "Disabled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const categoryTabs = useMemo(() => Object.entries(categories).map(([key, meta]) => ({ key, ...meta })), [categories]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Compliance</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Legal content</h1>
        <p className="mt-2 text-muted-foreground text-sm max-w-3xl">
          Manage every version-controlled piece of legal and regulatory content displayed across the platform —
          FCA disclaimers, policy pages and company info. Each entry is versioned, previewable, and can be
          audience-scoped (Professional / Retail / HNW / Self-Certified) for FCA disclaimers.
        </p>
      </div>

      {/* Category tabs */}
      <div className="flex items-center gap-1 border-b border-border mb-6 overflow-x-auto">
        {categoryTabs.map(({ key, label }) => {
          const Icon = CATEGORY_ICON[key] || FileText;
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`legal-tab-${key}`}
              className={`px-4 h-11 inline-flex items-center gap-2 text-sm border-b-2 -mb-px transition-colors font-medium ${
                active ? "border-emerald-600 text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon size={14} weight="fill" /> {label}
            </button>
          );
        })}
      </div>

      {categories[tab]?.description && (
        <div className="mb-4 text-sm text-muted-foreground max-w-3xl">
          {categories[tab].description}
        </div>
      )}

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {currentSlots.map((slot) => {
            const label = data[slot]?.label || slot;
            const meta = SLOT_META[slot] || {};
            const Icon = meta.icon || FileText;
            const audience = supportsAudience ? getActiveAudience(slot) : "professional";
            const audiences = data[slot]?.audiences || {};
            const g = audiences[audience] || { active: null, history: [] };
            const active = g.active;
            const history = g.history || [];
            return (
              <div key={slot} className="rounded-2xl border border-border bg-card p-5" data-testid={`disclaimer-card-${slot}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="h-9 w-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 grid place-items-center shrink-0">
                    <Icon size={18} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-lg leading-tight">{label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{meta.desc}</div>
                  </div>
                  {active && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${active.enabled ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>
                      {active.enabled ? "Active" : "Disabled"}
                    </span>
                  )}
                </div>

                {/* Audience tabs (FCA only) */}
                {supportsAudience && (
                  <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid={`audience-tabs-${slot}`}>
                    <UserFocus size={12} className="text-muted-foreground mr-1" />
                    {Object.keys(AUDIENCE_META).map((aud) => {
                      const m = AUDIENCE_META[aud];
                      const isActiveTab = audience === aud;
                      const hasVersion = !!audiences[aud]?.active;
                      return (
                        <button
                          key={aud}
                          onClick={() => setActiveAudience(slot, aud)}
                          data-testid={`audience-tab-${slot}-${aud}`}
                          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                            isActiveTab ? m.tone : "bg-transparent text-muted-foreground hover:text-foreground border border-border"
                          } ${!hasVersion && !isActiveTab ? "opacity-60" : ""}`}
                          title={hasVersion ? `${m.label} — has version` : `${m.label} — not configured yet`}
                        >
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {active ? (
                  <>
                    {active.title && (
                      <div className="text-sm font-display font-semibold mb-2 leading-tight">{active.title}</div>
                    )}
                    <div className="grid grid-cols-3 gap-2 mb-3 pt-3 border-t border-border">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Version</div>
                        <div className="font-display font-bold text-lg num-display">v{active.version}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Effective</div>
                        <div className="text-sm num-display">{formatDate(active.effective_at)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Prev. versions</div>
                        <div className="text-sm num-display">{history.length}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3 text-[11px] text-muted-foreground leading-relaxed border-t border-dashed border-border pt-2">
                      <div>
                        <span className="uppercase tracking-wider text-[9px] font-bold text-muted-foreground/70 block">Published by</span>
                        {active.published_by_name || "—"}
                        {active.published_at && <span className="opacity-70"> · {formatDate(active.published_at)}</span>}
                      </div>
                      <div>
                        <span className="uppercase tracking-wider text-[9px] font-bold text-muted-foreground/70 block">Last modified by</span>
                        {active.last_modified_by_name || active.published_by_name || "—"}
                        {active.last_modified_at && <span className="opacity-70"> · {formatDate(active.last_modified_at)}</span>}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground leading-relaxed line-clamp-4 whitespace-pre-line mb-3">
                      {active.body}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => setPreview(active)} data-testid={`preview-${slot}`}>
                        <Eye size={13} className="mr-1" /> Preview
                      </Button>
                      <Button size="sm" onClick={() => openEdit(slot, audience)} data-testid={`edit-disclaimer-${slot}`}>
                        <PencilSimple size={13} className="mr-1" /> New version
                      </Button>
                      <label className="ml-auto flex items-center gap-2 text-xs">
                        <Switch checked={active.enabled} onCheckedChange={(v) => toggleEnabled(active.id, v)} />
                        Enabled
                      </label>
                    </div>
                    {history.length > 0 && (
                      <details className="mt-3 pt-3 border-t border-border/60">
                        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1.5">
                          <Clock size={12} /> Version history ({history.length})
                        </summary>
                        <div className="mt-2 space-y-1.5">
                          {history.map((h) => (
                            <button
                              key={h.id}
                              onClick={() => setPreview(h)}
                              className="w-full flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-secondary/50 text-left"
                            >
                              <span className="font-mono w-12 shrink-0">v{h.version}</span>
                              <span className="text-muted-foreground">Effective {formatDate(h.effective_at)}</span>
                              <span className="opacity-50">·</span>
                              <span className="text-muted-foreground truncate">Published {formatDate(h.published_at || h.created_at)}</span>
                            </button>
                          ))}
                        </div>
                      </details>
                    )}
                  </>
                ) : (
                  <div className="py-6 text-center">
                    <div className="text-sm text-muted-foreground italic mb-3">
                      {supportsAudience ? "No version published for this audience yet." : "No version published yet."}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openEdit(slot, audience)} data-testid={`create-disclaimer-${slot}-${audience}`}>
                      <PencilSimple size={13} className="mr-1" /> Create first version
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!editSlot} onOpenChange={(o) => !o && setEditSlot(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              New version — {data[editSlot]?.label || editSlot}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {supportsAudience ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Audience</Label>
                  <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                    <SelectTrigger className="mt-1" data-testid="disclaimer-audience-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(AUDIENCE_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Title (optional)</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" />
                </div>
              </div>
            ) : (
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Version (optional)</Label>
                <Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="Auto-increment if blank" className="mt-1 font-mono" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Effective date</Label>
                <Input type="date" value={form.effective_at} onChange={(e) => setForm({ ...form, effective_at: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Body</Label>
              <Textarea
                rows={14}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="mt-1 font-mono text-xs leading-relaxed"
                data-testid="disclaimer-body-input"
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                Line breaks preserved. Use <code>{"{{company_name}}"}</code> to insert the current company name automatically. Use <code>##&nbsp;Section</code> for headings.
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Internal notes (optional)</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Updated per legal review Sep-26" className="mt-1" />
            </div>
            <label className="flex items-center gap-2 text-sm pt-2">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              Enable on publish
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSlot(null)}>Cancel</Button>
            <Button onClick={publish} disabled={busy} data-testid="publish-disclaimer-btn" className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
              {busy ? "Publishing…" : "Publish new version"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { setPreview(null); setShowDiff(false); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">{preview?.title || `Preview — v${preview?.version}`}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[11px] text-muted-foreground">
              v{preview?.version} · {preview?.audience && AUDIENCE_META[preview.audience]?.label} · Effective {preview && formatDate(preview.effective_at)}
            </div>
            {preview && (() => {
              const slot = preview.slot;
              const aud = preview.audience || "professional";
              const audienceGroup = data[slot]?.audiences?.[aud];
              if (!audienceGroup) return null;
              const versions = [audienceGroup.active, ...(audienceGroup.history || [])].filter(Boolean);
              const idx = versions.findIndex((v) => v.id === preview.id);
              const prev = idx >= 0 && idx < versions.length - 1 ? versions[idx + 1] : null;
              return prev ? (
                <Button variant="outline" size="sm" onClick={() => setShowDiff((v) => !v)}>
                  <ArrowsHorizontal size={13} className="mr-1" /> {showDiff ? "Hide diff" : `Diff vs v${prev.version}`}
                </Button>
              ) : null;
            })()}
          </div>
          {showDiff && preview && (() => {
            const slot = preview.slot;
            const aud = preview.audience || "professional";
            const audienceGroup = data[slot]?.audiences?.[aud];
            if (!audienceGroup) return null;
            const versions = [audienceGroup.active, ...(audienceGroup.history || [])].filter(Boolean);
            const idx = versions.findIndex((v) => v.id === preview.id);
            const prev = idx >= 0 && idx < versions.length - 1 ? versions[idx + 1] : null;
            if (!prev) return null;
            const rows = paragraphDiff(prev.body, preview.body);
            const removed = rows.filter((r) => r.status === "removed").length;
            const added = rows.filter((r) => r.status === "added").length;
            return (
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="px-3 py-2 bg-secondary/40 text-[10px] uppercase tracking-wider font-bold text-muted-foreground flex justify-between">
                  <span>Diff · v{prev.version} → v{preview.version}</span>
                  <span><span className="text-red-600">{removed} removed</span> · <span className="text-emerald-600">{added} added</span></span>
                </div>
                <div className="max-h-[45vh] overflow-y-auto text-sm p-3 space-y-2">
                  {rows.map((r, i) => (
                    <div key={i} className={`px-3 py-2 rounded whitespace-pre-line leading-relaxed ${
                      r.status === "added" ? "bg-emerald-50 dark:bg-emerald-950/30 border-l-4 border-emerald-500"
                      : r.status === "removed" ? "bg-red-50 dark:bg-red-950/30 border-l-4 border-red-500 line-through decoration-red-500/30"
                      : "bg-transparent text-muted-foreground border-l-4 border-transparent"
                    }`}>
                      <span className={`font-mono text-[10px] mr-2 ${r.status === "added" ? "text-emerald-700" : r.status === "removed" ? "text-red-700" : "text-muted-foreground/40"}`}>
                        {r.status === "added" ? "+ " : r.status === "removed" ? "− " : "  "}
                      </span>
                      {r.text}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          {!showDiff && (
            <div className="rounded-lg border border-border p-4 text-sm leading-relaxed whitespace-pre-line max-h-[60vh] overflow-y-auto">
              {preview?.body}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
